const locale = navigator.language || 'en';

export function formatMoney(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amountMinor / 100);
}

export function formatDate(input: string | null | undefined): string {
  if (!input) return '—';
  return new Date(input).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function formatDateTime(input: string | null | undefined): string {
  if (!input) return '—';
  return new Date(input).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatRelative(input: string | null | undefined): string {
  if (!input) return 'never';
  const seconds = Math.round((new Date(input).getTime() - Date.now()) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return 'just now';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

export function daysUntil(input: string | null | undefined): number | null {
  if (!input) return null;
  return Math.ceil((new Date(input).getTime() - Date.now()) / 86_400_000);
}

export function periodLabel(months: number): string {
  if (months === 12) return 'year';
  if (months === 1) return 'month';
  return `${months} months`;
}

/** Display order for plan features; anything unlisted follows, alphabetically. */
const FEATURE_ORDER = [
  'remote_control',
  'max_devices',
  'multi_device',
  'cloud_sync',
  'cloud_storage_gb',
  'sync_history_days',
  'sharing',
  'ai_minutes_monthly',
  'organization_features',
  'support_tier',
];

export function featureLines(features: Record<string, boolean | number | string>): string[] {
  const rank = (key: string) => {
    const index = FEATURE_ORDER.indexOf(key);
    return index === -1 ? FEATURE_ORDER.length : index;
  };
  return Object.entries(features)
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([key, value]) => describeFeature(key, value))
    .filter((line): line is string => Boolean(line));
}

/** Human wording for the entitlement catalogue; unknown keys fall back to the key. */
export function describeFeature(key: string, value: boolean | number | string): string | null {
  switch (key) {
    case 'remote_control':
      return value ? 'Remote control' : null;
    case 'cloud_sync':
      return value ? 'Cloud sync of layouts and settings' : null;
    case 'multi_device':
      return value ? 'Several devices at the same time' : null;
    case 'max_devices':
      return value === -1 ? 'Unlimited devices' : `Up to ${value} devices`;
    case 'cloud_storage_gb':
      return typeof value === 'number' && value > 0 ? `${value >= 1000 ? `${value / 1000} TB` : `${value} GB`} cloud storage` : null;
    case 'sync_history_days':
      return typeof value === 'number' && value > 0 ? `${value}-day version history` : null;
    case 'sharing':
      return value ? 'Read-only sharing links' : null;
    case 'ai_minutes_monthly':
      return typeof value === 'number' && value > 0 ? `${value.toLocaleString(locale)} AI minutes a month` : null;
    case 'organization_features':
      return value ? 'Team management and shared billing' : null;
    case 'support_tier':
      return typeof value === 'string' ? `${value.charAt(0).toUpperCase()}${value.slice(1)} support` : null;
    default:
      return value === true ? key.replace(/_/g, ' ') : null;
  }
}

export const PLATFORM_LABELS: Record<string, string> = {
  windows: 'Windows',
  macos: 'macOS',
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
};
